/* =====================================================================
 * DMChatThread — 오른쪽 메시지 스레드 (채팅 영역)
 *
 * max-width 제한 + 사이드바 동일 아바타 팔레트
 * ===================================================================== */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ChevronUp } from 'lucide-react';
import type { DMAgentType } from '@auw/workspace-utils/types/dm';
import { getAgentInfo } from '@auw/workspace-utils/types/dm';
import { EmptyState } from '@auw/workspace-ui';
import { useDMStore } from './hooks/useDMStore';
import { useAutoScroll } from '@auw/workspace-utils';
import DMMessageBubble from './DMMessageBubble';
import DMTypingIndicator from './DMTypingIndicator';

interface Props {
  agentType: DMAgentType;
  transparentInput?: boolean;
}

const DMChatThread: React.FC<Props> = ({ agentType, transparentInput = false }) => {
  const { t } = useTranslation('common');
  const {
    messages,
    hasMore,
    isLoading,
    typingAgents,
    loadMessages,
  } = useDMStore();

  const agent = getAgentInfo(agentType);
  const isSystemApp = agentType === 'system_app';
  const agentMessages = messages[agentType];
  const isTyping = typingAgents.has(agentType);
  const lastMessage = agentMessages[agentMessages.length - 1] ?? null;
  const autoScrollDeps = [
    lastMessage?.id ?? 'empty',
    lastMessage?.content.length ?? 0,
    isTyping ? 1 : 0,
  ];
  const { containerRef: scrollRef, handleScroll, scrollToBottom } = useAutoScroll(
    autoScrollDeps,
    isTyping,
    agentType,
  );

  const handleLoadMore = useCallback(() => {
    loadMessages(agentType, true);
  }, [agentType, loadMessages]);

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderInner>
          <HeaderAppLogoWrap>
            <HeaderAppLogo src="/agent_logo.png" alt="App" />
          </HeaderAppLogoWrap>
          <HeaderInfo>
            <AgentName>{agent.name}</AgentName>
            <AgentDesc>{agent.description}</AgentDesc>
          </HeaderInfo>
        </HeaderInner>
      </Header>

      {/* Messages */}
      <MessagesArea ref={scrollRef} onScroll={handleScroll}>
        <MessagesInner $systemApp={isSystemApp}>
          {hasMore[agentType] && (
            <LoadMoreBtn onClick={handleLoadMore} disabled={isLoading}>
              <ChevronUp size={14} />
              {isLoading ? t('dm.loading') : t('dm.loadPreviousMessages')}
            </LoadMoreBtn>
          )}

          {agentMessages.length === 0 && !isLoading && (
            <EmptyState
              icon={
                <EmptyAvatar>
                  <EmptyAvatarImg src="/agent_logo.png" alt={agent.name} />
                </EmptyAvatar>
              }
              description={t('dm.startConversationWith', { name: agent.name })}
            />
          )}

          {agentMessages.map((msg) => (
            <DMMessageBubble key={msg.id} message={msg} />
          ))}

          {isTyping && <DMTypingIndicator agentType={agentType} />}
          {isSystemApp && agentMessages.length > 0 && <SystemBottomSpacer />}
        </MessagesInner>
      </MessagesArea>

    </Container>
  );
};

export default DMChatThread;

/* ---- Styled ---- */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: transparent;
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: ${({ theme }) => theme.dm.threadMaxWidth};
  padding: ${({ theme }) => theme.dm.threadHeaderPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: ${({ theme }) => theme.dmMobile.threadMaxWidth};
    padding: ${({ theme }) => theme.dmMobile.threadHeaderPad};
  }
`;

/** 사이드바 동일 팔레트 아바타 */
const HeaderAvatar = styled.div`
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.threadHeaderAvatarSize};
  height: ${({ theme }) => theme.dm.threadHeaderAvatarSize};
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.mode === 'dark' ? 'linear-gradient(145deg, #434345, #38383a)' : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) => theme.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-weight: 700;
  font-size: ${({ theme }) => theme.dm.threadHeaderAvatarFontSize};
  line-height: 1;
  user-select: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.dmMobile.threadHeaderAvatarSize};
    height: ${({ theme }) => theme.dmMobile.threadHeaderAvatarSize};
    font-size: ${({ theme }) => theme.dmMobile.threadHeaderAvatarFontSize};
  }
`;

const HeaderAppLogoWrap = styled.div`
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.threadHeaderAvatarSize};
  height: ${({ theme }) => theme.dm.threadHeaderAvatarSize};
  border-radius: 22%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.mode === 'dark' ? 'linear-gradient(145deg, #434345, #38383a)' : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) => theme.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.dmMobile.threadHeaderAvatarSize};
    height: ${({ theme }) => theme.dmMobile.threadHeaderAvatarSize};
  }
`;

const HeaderAppLogo = styled.img`
  width: 76%;
  height: 76%;
  object-fit: contain;
  display: block;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const AgentName = styled.div`
  font-size: ${({ theme }) => theme.dm.threadHeaderNameFontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.dmMobile.threadHeaderNameFontSize};
  }
`;

const AgentDesc = styled.div`
  font-size: ${({ theme }) => theme.dm.threadHeaderDescFontSize};
  color: ${({ theme }) => theme.colors.textMuted};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.dmMobile.threadHeaderDescFontSize};
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 2px;
  }
`;

const MessagesInner = styled.div<{ $systemApp: boolean }>`
  width: 100%;
  max-width: ${({ theme }) => theme.dm.threadMaxWidth};
  padding: ${({ theme }) => theme.dm.messagesPad};
  padding-bottom: ${({ $systemApp }) => ($systemApp ? '16px' : '16px')};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.dm.messagesGap};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: ${({ theme }) => theme.dmMobile.threadMaxWidth};
    padding: ${({ theme }) => theme.dmMobile.messagesPad};
    padding-bottom: ${({ $systemApp }) => ($systemApp ? '12px' : '12px')};
    gap: ${({ theme }) => theme.dmMobile.messagesGap};
  }
`;

const SystemBottomSpacer = styled.div`
  width: 100%;
  height: 20px;
  flex: 0 0 20px;
  pointer-events: none;
`;

const LoadMoreBtn = styled.button`
  align-self: center;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 16px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.dm.timeFontSize};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  margin-bottom: 8px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyAvatar = styled.div`
  width: ${({ theme }) => theme.dm.emptyAvatarSize};
  height: ${({ theme }) => theme.dm.emptyAvatarSize};
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.mode === 'dark' ? 'linear-gradient(145deg, #434345, #38383a)' : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) => theme.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-weight: 700;
  font-size: ${({ theme }) => theme.dm.emptyAvatarFontSize};
  line-height: 1;
  user-select: none;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.dmMobile.emptyAvatarSize};
    height: ${({ theme }) => theme.dmMobile.emptyAvatarSize};
    font-size: ${({ theme }) => theme.dmMobile.emptyAvatarFontSize};
  }
`;

const EmptyAvatarImg = styled.img`
  width: 76%;
  height: 76%;
  object-fit: contain;
  display: block;
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 24px;
`;

const InputInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.dm.threadMaxWidth};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: ${({ theme }) => theme.dmMobile.threadMaxWidth};
  }
`;
